/**
 * Wall Strip Renderer — Unified strip-splitting for wall panels and exterior cladding.
 *
 * Both interior panels (`addPanelWithOpenings`) and exterior cladding
 * (`addExteriorCladdingWall`) use the same algorithm:
 *   1. Sort openings along the wall
 *   2. Render strips below/above each opening
 *   3. Render full-height strips in the gaps between openings
 *
 * This module provides the generic `renderWallStrips()` that both call.
 */
import type * as THREE from "three";
import type { WallOpening } from "../domain/framing/generateFramedWall";
import type { RectRoofGeometry } from "../domain/roof/rectRoofGeometry";
import {
	ENGINE_FACADE_FURRING_CC_M,
	ENGINE_CLADDING_COV_M,
	ENGINE_PANEL_T_M as PANEL_T,
	ENGINE_STUD_D_M as STUD_D,
	ENGINE_WALL_INSULATION_INNER_GAP_M,
} from "./constants";
import { addBox, addSizedBox, addSlopedTopBox } from "./geometry";
import {
	EXTERIOR_TEXTURE_PLANKS_PER_TILE,
	MATERIAL_TEXTURE_PROFILES,
} from "./materialProfiles";
import { matGlass } from "./materials";

/**
 * Reference span, in metres, that one full UV unit of an OUTER wall surface
 * covers. The exterior profile tiles the albedo `repeat.x` times across
 * UV [0,1], so spanning `repeat.x × planks × boardW` metres per UV unit
 * gives every cladding board its real-world cover width on every sauna
 * size. Outer UVs used to normalize by the sauna length instead, which
 * squeezed 24 boards onto a 2 m wall (~83 mm each — reads as far too
 * narrow next to real facade panel).
 */
export const EXTERIOR_UV_REFERENCE_SPAN_M =
	MATERIAL_TEXTURE_PROFILES.exterior.repeat[0] *
	EXTERIOR_TEXTURE_PLANKS_PER_TILE *
	ENGINE_CLADDING_COV_M;

// The square atlas must cover the same physical span along and across its
// eight boards. Compensate the different Y repeat instead of normalizing
// grain to the wall height (which squashed knots and changed with the roof).
const EXTERIOR_UV_REFERENCE_HEIGHT_M =
	MATERIAL_TEXTURE_PROFILES.exterior.repeat[1] *
	EXTERIOR_TEXTURE_PLANKS_PER_TILE *
	ENGINE_CLADDING_COV_M;

// ─── Types ──────────────────────────────────────────────────

export type WallName = "front" | "back" | "left" | "right";

export interface StripRenderContext {
	wall: WallName;
	W: number;
	L: number;
	wallBase: number;
	roofGeometry: RectRoofGeometry;
	/** Perpendicular offset outward from the stud face (0 for interior layers). */
	offset: number;
	/**
	 * Build-out INWARD from the stud's inner face, in metres, before this
	 * layer starts. The interior is built regel → folie → liggande glespanel →
	 * bastupanel, so each layer passes the summed depth of everything behind
	 * it (folie: 0, gles: folieT, panel: folieT + glesD). Defaults to 0.
	 */
	innerOffset?: number;
	/**
	 * Clip inner-surface layers to this span range (metres along the wall,
	 * from the wall's start). Used by the two-room engine to keep the
	 * bastufolie inside the sauna room — the vapour barrier belongs in the hot
	 * room only, which is also exactly what the BOM buys. Defaults to the full
	 * stud-inset inner range.
	 */
	innerAxisRange?: { start: number; end: number };
	/** Material for the solid strips. */
	material: THREE.Material;
	group: THREE.Group;
	/** If true, glazed openings get a glass pane. */
	renderGlass?: boolean;
	renderDoorGlass?: boolean;
	/**
	 * Extend outer-surface strips this far past both wall ends, in metres.
	 * Used by the exterior cladding so the panel wraps the corner build-up
	 * (wind barrier + furring) the way a real facade is clad — panel first,
	 * covering the framing all the way around the corner, knutbräda on top.
	 * The extension renders as its own strip segment so a gable wall's tail
	 * strip keeps its exact slope; past the wall end the roof profile clamps
	 * flat, which is what the corner boards assume too.
	 */
	cornerExtensionM?: number;
}

type SurfaceKind = "inner" | "outer";
type VerticalSegment = { start: number; end: number };

interface WallOrientation {
	spanAxis: "x" | "z";
	spanStart: (ctx: StripRenderContext) => number;
	wallPlane: (ctx: StripRenderContext) => number;
	outwardSign: 1 | -1;
}

const WALL_ORIENTATIONS: Record<WallName, WallOrientation> = {
	front: {
		spanAxis: "x",
		spanStart: (ctx) => -ctx.W / 2,
		wallPlane: (ctx) => ctx.L / 2,
		outwardSign: 1,
	},
	back: {
		spanAxis: "x",
		spanStart: (ctx) => -ctx.W / 2,
		wallPlane: (ctx) => -ctx.L / 2,
		outwardSign: -1,
	},
	left: {
		spanAxis: "z",
		spanStart: (ctx) => -ctx.L / 2,
		wallPlane: (ctx) => -ctx.W / 2,
		outwardSign: -1,
	},
	right: {
		spanAxis: "z",
		spanStart: (ctx) => -ctx.L / 2,
		wallPlane: (ctx) => ctx.W / 2,
		outwardSign: 1,
	},
};

function getWallHeightAtLocalCenter(
	ctx: StripRenderContext,
	localX: number,
): number {
	return ctx.roofGeometry.getWallHeightAt(ctx.wall, localX);
}

function getWallHeightAtRangeEdge(
	ctx: StripRenderContext,
	localX: number,
): number {
	return ctx.roofGeometry.getWallHeightAt(ctx.wall, localX);
}

function getWallOrientation(ctx: StripRenderContext): WallOrientation {
	return WALL_ORIENTATIONS[ctx.wall];
}

function getWallWorldPosition(
	ctx: StripRenderContext,
	localCenter: number,
	normalCenter: number,
) {
	const orientation = getWallOrientation(ctx);
	const spanCenter = orientation.spanStart(ctx) + localCenter;

	return orientation.spanAxis === "x"
		? { x: spanCenter, z: normalCenter }
		: { x: normalCenter, z: spanCenter };
}

function getWallBoxDimensions(
	ctx: StripRenderContext,
	spanSize: number,
	height: number,
	depth: number,
) {
	return getWallOrientation(ctx).spanAxis === "x"
		? { w: spanSize, h: height, d: depth }
		: { w: depth, h: height, d: spanSize };
}

function getOuterWallNormalCenter(ctx: StripRenderContext): number {
	const orientation = getWallOrientation(ctx);
	return orientation.wallPlane(ctx) + orientation.outwardSign * ctx.offset;
}

/**
 * Centre of a layer sitting on the INSIDE of the wall, `ctx.innerOffset` past
 * the stud's inner face. Every interior layer (folie, liggande glespanel,
 * bastupanel) resolves its normal position through here, so the layer order is
 * expressed once as the offset each caller passes in — there is no separate
 * formula that can drift. (The interior battens used to have their own
 * `STUD_D - stripDepth / 2` variant, which buried them *inside* the stud
 * volume as invisible duplicates rather than making them a layer.)
 */
function getInnerSurfaceNormalCenter(
	ctx: StripRenderContext,
	thickness: number,
): number {
	const orientation = getWallOrientation(ctx);
	return (
		orientation.wallPlane(ctx) -
		orientation.outwardSign *
			(STUD_D + (ctx.innerOffset ?? 0) + thickness / 2 - ctx.offset)
	);
}

function addWallAlignedBox(
	ctx: StripRenderContext,
	localCenter: number,
	yCenter: number,
	spanSize: number,
	height: number,
	depth: number,
	material: THREE.Material,
	normalCenter: number,
	options?: {
		slopeProfile?: ReturnType<typeof resolveSlopeProfile>;
		useSizedGeometry?: boolean;
	},
) {
	const { x, z } = getWallWorldPosition(ctx, localCenter, normalCenter);
	const { w, h, d } = getWallBoxDimensions(ctx, spanSize, height, depth);

	if (options?.slopeProfile) {
		return addSlopedTopBox(
			w,
			h,
			d,
			material,
			x,
			yCenter,
			z,
			ctx.group,
			options.slopeProfile.pitchRad,
			options.slopeProfile.axis,
		);
	}

	if (options?.useSizedGeometry) {
		return addSizedBox(w, h, d, material, x, yCenter, z, ctx.group);
	}

	return addBox(w, h, d, material, x, yCenter, z, ctx.group);
}

function alignWallSurfaceUvs(
	geometry: THREE.BufferGeometry,
	ctx: StripRenderContext,
	localX: number,
	localY: number,
	spanSize: number,
	height: number,
	surface: SurfaceKind,
) {
	const orientation = getWallOrientation(ctx);
	const positions = geometry.getAttribute("position");
	const normals = geometry.getAttribute("normal");
	const uvs = geometry.getAttribute("uv");
	// Outer surfaces (exterior cladding) map UVs to physical metres so a
	// board keeps its real cover width regardless of sauna size; interior
	// panels keep the shared sauna-length reference frame.
	const referenceSpan =
		surface === "outer" ? EXTERIOR_UV_REFERENCE_SPAN_M : ctx.L;
	const referenceHeight =
		surface === "outer"
			? EXTERIOR_UV_REFERENCE_HEIGHT_M
			: ctx.roofGeometry.getMaxWallHeight("left");

	for (let index = 0; index < positions.count; index += 1) {
		const normal =
			orientation.spanAxis === "x" ? normals.getZ(index) : normals.getX(index);
		if (Math.abs(normal) < 0.9) continue;

		const spanPosition =
			orientation.spanAxis === "x"
				? positions.getX(index)
				: positions.getZ(index);
		uvs.setXY(
			index,
			(localX + spanPosition + spanSize / 2) / referenceSpan,
			(localY + positions.getY(index) + height / 2) / referenceHeight,
		);
	}

	uvs.needsUpdate = true;
}

function splitRangeAtProfileBreaks(
	ctx: StripRenderContext,
	localX: number,
	stripW: number,
) {
	const segments = [{ localX, stripW }];
	const isFrontOrBackGable =
		ctx.roofGeometry.roofConfig.kind === "gable" &&
		(ctx.wall === "front" || ctx.wall === "back");

	if (!isFrontOrBackGable) {
		return segments;
	}

	const ridgeLocalX = ctx.W / 2;
	if (localX >= ridgeLocalX || localX + stripW <= ridgeLocalX) {
		return segments;
	}

	return [
		{ localX, stripW: ridgeLocalX - localX },
		{ localX: ridgeLocalX, stripW: localX + stripW - ridgeLocalX },
	];
}

function getSurfaceAxisRange(ctx: StripRenderContext, surface: SurfaceKind) {
	const wallLM = getWallLength(ctx);

	if (surface === "inner") {
		const full = { start: STUD_D, end: wallLM - STUD_D };
		if (!ctx.innerAxisRange) return full;
		return {
			start: Math.max(full.start, ctx.innerAxisRange.start),
			end: Math.min(full.end, ctx.innerAxisRange.end),
		};
	}

	const cornerExtension = ctx.cornerExtensionM ?? 0;
	return {
		start: -cornerExtension,
		end: wallLM + cornerExtension,
	};
}

function resolveSlopeProfile(
	ctx: StripRenderContext,
	localX: number,
	stripW: number,
) {
	const startHeight = getWallHeightAtRangeEdge(ctx, localX);
	const endHeight = getWallHeightAtRangeEdge(ctx, localX + stripW);
	const delta = endHeight - startHeight;
	if (Math.abs(delta) < 0.001) {
		return null;
	}

	const span = stripW;
	const pitchRad = Math.atan(delta / Math.max(span, 0.0001));
	return {
		axis:
			ctx.wall === "front" || ctx.wall === "back"
				? ("x" as const)
				: ("z" as const),
		pitchRad,
	};
}

function addSurfaceStripMesh(
	ctx: StripRenderContext,
	localX: number,
	localY: number,
	stripW: number,
	stripH: number,
	thickness: number,
	mat: THREE.Material,
	surface: SurfaceKind,
	namePrefix: string,
) {
	const { wall, wallBase } = ctx;
	const slopeProfile = resolveSlopeProfile(ctx, localX, stripW);
	const wallHeight = getWallHeightAtLocalCenter(ctx, localX + stripW / 2);
	const clampedH = Math.min(stripH, wallHeight - localY);
	if (clampedH <= 0) return;

	const mesh = addWallAlignedBox(
		ctx,
		localX + stripW / 2,
		wallBase + localY + clampedH / 2,
		stripW,
		clampedH,
		thickness,
		mat,
		surface === "inner"
			? getInnerSurfaceNormalCenter(ctx, thickness)
			: getOuterWallNormalCenter(ctx),
		{
			slopeProfile:
				slopeProfile && localY + clampedH >= wallHeight - 0.01
					? slopeProfile
					: undefined,
			useSizedGeometry: true,
		},
	);
	alignWallSurfaceUvs(
		mesh.geometry,
		ctx,
		localX,
		localY,
		stripW,
		clampedH,
		surface,
	);
	mesh.name = `${namePrefix}/${wall}-${surface}-${localX.toFixed(2)}-${localY.toFixed(2)}`;
}

function subtractVerticalRange(
	segments: VerticalSegment[],
	openingStart: number,
	openingEnd: number,
) {
	for (let index = segments.length - 1; index >= 0; index -= 1) {
		const segment = segments[index];
		if (openingEnd <= segment.start || openingStart >= segment.end) continue;

		segments.splice(index, 1);
		if (openingStart > segment.start + 0.01) {
			segments.splice(index, 0, { start: segment.start, end: openingStart });
		}
		if (openingEnd < segment.end - 0.01) {
			segments.splice(
				index + (openingStart > segment.start + 0.01 ? 1 : 0),
				0,
				{
					start: openingEnd,
					end: segment.end,
				},
			);
		}
	}
}

function splitVerticalSegmentsAroundOpenings(
	wallOpenings: WallOpening[],
	localCenter: number,
	wallHeight: number,
	halfStrip: number,
): VerticalSegment[] {
	const segments: VerticalSegment[] = [{ start: 0, end: wallHeight }];

	for (const opening of wallOpenings) {
		const startX = opening.xMm / 1000;
		const endX = startX + opening.widthMm / 1000;
		if (localCenter <= startX + halfStrip || localCenter >= endX - halfStrip)
			continue;

		const openingStart = opening.yMm / 1000;
		const openingEnd = openingStart + opening.heightMm / 1000;
		subtractVerticalRange(segments, openingStart, openingEnd);
	}

	return segments;
}

function renderStudAlignedStrips(
	ctx: StripRenderContext,
	studPositions: number[],
	wallOpenings: WallOpening[],
	stripWidth: number,
	stripDepth: number,
	material: THREE.Material,
	options: {
		namePrefix: string;
		getNormalCenter: (ctx: StripRenderContext, stripDepth: number) => number;
	},
) {
	const { wall, wallBase } = ctx;
	const halfStrip = stripWidth / 2;
	const normalCenter = options.getNormalCenter(ctx, stripDepth);
	const uniquePositions = [...new Set(studPositions)].sort((a, b) => a - b);

	for (const localCenter of uniquePositions) {
		const wallHeight = getWallHeightAtLocalCenter(ctx, localCenter);
		const segments = splitVerticalSegmentsAroundOpenings(
			wallOpenings,
			localCenter,
			wallHeight,
			halfStrip,
		);

		for (const segment of segments) {
			const segmentHeight = segment.end - segment.start;
			if (segmentHeight <= 0.01) continue;

			const mesh = addWallAlignedBox(
				ctx,
				localCenter,
				wallBase + segment.start + segmentHeight / 2,
				stripWidth,
				segmentHeight,
				stripDepth,
				material,
				normalCenter,
			);
			mesh.name = `${options.namePrefix}/${wall}-${localCenter.toFixed(2)}-${segment.start.toFixed(2)}`;
		}
	}
}

function renderOpeningAwareStrips(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	wallH: number,
	thickness: number,
	surface: SurfaceKind,
	renderGlass = false,
	namePrefix = "detail/panel",
) {
	const wallLM = getWallLength(ctx);
	const surfaceRange = getSurfaceAxisRange(ctx, surface);

	const addStripSegment = (
		localX: number,
		localY: number,
		stripW: number,
		stripH: number,
		material: THREE.Material,
		stripNamePrefix: string,
	) => {
		for (const segment of splitRangeAtProfileBreaks(ctx, localX, stripW)) {
			const clippedStart = Math.max(segment.localX, surfaceRange.start);
			const clippedEnd = Math.min(
				segment.localX + segment.stripW,
				surfaceRange.end,
			);
			const clippedWidth = clippedEnd - clippedStart;

			if (clippedWidth <= 0.01) continue;

			addSurfaceStripMesh(
				ctx,
				clippedStart,
				localY,
				clippedWidth,
				stripH,
				thickness,
				material,
				surface,
				stripNamePrefix,
			);
		}
	};

	const cornerExtension = ctx.cornerExtensionM ?? 0;
	if (cornerExtension > 0.001) {
		addStripSegment(
			-cornerExtension,
			0,
			cornerExtension,
			wallH,
			ctx.material,
			namePrefix,
		);
		addStripSegment(
			wallLM,
			0,
			cornerExtension,
			wallH,
			ctx.material,
			namePrefix,
		);
	}

	if (wallOpenings.length === 0) {
		addStripSegment(0, 0, wallLM, wallH, ctx.material, namePrefix);
		return;
	}

	const sorted = wallOpenings.toSorted((a, b) => a.xMm - b.xMm);

	for (const op of sorted) {
		const opX = op.xMm / 1000;
		const opY = op.yMm / 1000;
		const opW = op.widthMm / 1000;
		const opH = op.heightMm / 1000;

		if (opY > 0.01) {
			addStripSegment(opX, 0, opW, opY, ctx.material, namePrefix);
		}
		const topGap = wallH - (opY + opH);
		if (topGap > 0.01) {
			addStripSegment(opX, opY + opH, opW, topGap, ctx.material, namePrefix);
		}

		// Door leaves own their glazing; an opening-wide pane intersects the leaf.
		if (renderGlass && (op.type === "window" || (op.glazed && ctx.renderDoorGlass !== false))) {
			addStripSegment(
				opX,
				opY,
				opW,
				opH,
				matGlass,
				op.id.startsWith("panorama-")
					? "detail/panorama-glass"
					: // A helglasdörr's glass is part of the door the Bastudörr
						// row buys, not a window (issue 160).
						op.type === "door"
						? "detail/door-glass"
						: "detail/opening-glass",
			);
		}
	}

	let prevEnd = 0;
	for (const op of sorted) {
		const opX = op.xMm / 1000;
		const gap = opX - prevEnd;
		if (gap > 0.01) {
			addStripSegment(prevEnd, 0, gap, wallH, ctx.material, namePrefix);
		}
		prevEnd = opX + op.widthMm / 1000;
	}
	const tailGap = wallLM - prevEnd;
	if (tailGap > 0.01) {
		addStripSegment(prevEnd, 0, tailGap, wallH, ctx.material, namePrefix);
	}
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Render interior panel strips on a wall, splitting around openings.
 * Glazed openings get a glass pane if renderGlass is true (default).
 */
export function renderPanelStrips(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	wallH: number,
) {
	renderOpeningAwareStrips(
		ctx,
		wallOpenings,
		wallH,
		PANEL_T,
		"inner",
		ctx.renderGlass !== false,
		"detail/panel",
	);
}

/**
 * Render exterior cladding strips on a wall, splitting around openings.
 */
export function renderExteriorCladdingStrips(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	wallH: number,
	cladT: number,
) {
	renderOpeningAwareStrips(
		ctx,
		wallOpenings,
		wallH,
		cladT,
		"outer",
		false,
		"detail/cladding",
	);
}

export function renderOuterLayerStrips(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	wallH: number,
	thickness: number,
) {
	renderOpeningAwareStrips(
		ctx,
		wallOpenings,
		wallH,
		thickness,
		"outer",
		false,
		"detail/wind-barrier",
	);
}

export function renderFurringStrips(
	ctx: StripRenderContext,
	studPositions: number[],
	wallOpenings: WallOpening[],
	stripWidth: number,
	stripDepth: number,
	material: THREE.Material,
) {
	renderStudAlignedStrips(
		ctx,
		studPositions,
		wallOpenings,
		stripWidth,
		stripDepth,
		material,
		{
			namePrefix: "detail/furring",
			getNormalCenter: getOuterWallNormalCenter,
		},
	);
}

/**
 * Render HORIZONTAL nailing battens (liggande spikläkt) up the wall height.
 *
 * Standing exterior panel with an air gap must be built as korsläkt: standing
 * ströläkt on the studs (holds the drained air gap open — `renderFurringStrips`)
 * → these lying spikläkt at c/c ~600 up the height → standing panel nailed into
 * the lying battens. Without this layer every panel board that doesn't happen to
 * land over a stud line has nothing to nail into.
 *
 * Rows break around openings (the height-direction analogue of
 * `renderOpeningAwareStrips`) and each row is clipped along the span to where
 * the roof profile still leaves wall (`getWallHeightAt`), so gable peaks and
 * mono/pulpet slopes are followed the same way the panel follows them. Rows run
 * all the way up to the wall's HIGHEST point (gable ridge / mono high eave) —
 * `clipRowToRoofProfile` then trims each row to the sub-spans where wall exists,
 * so the gable triangle and the mono-pitch rising wedge get battens too (the
 * panel there would otherwise have nothing to nail into). The top row is pinned
 * just under the max height so the highest panel board still crosses a batten.
 */
export function renderHorizontalFurringStrips(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	stripWidth: number,
	stripDepth: number,
	material: THREE.Material,
) {
	const cornerExtension = ctx.cornerExtensionM ?? 0;
	renderLyingBattenRows(ctx, wallOpenings, stripWidth, stripDepth, material, {
		namePrefix: "detail/furring-horizontal",
		rowSpacing: ENGINE_FACADE_FURRING_CC_M,
		normalCenter: getOuterWallNormalCenter(ctx),
		axisRange: {
			start: -cornerExtension,
			end: getWallLength(ctx) + cornerExtension,
		},
	});
}

/**
 * Render the LYING glespanel / inv. spikläkt on the inside of a wall.
 *
 * Same korsläkt reasoning as the facade: the bastupanel stands, so the battens
 * it is nailed to must lie across it. The rows sit `ctx.innerOffset` past the
 * stud face (i.e. on top of the bastufolie) and the panel then sits on top of
 * them. The span is inset by one stud depth at each end, matching the inner
 * panel surface range, so a wall's battens butt into — rather than run through
 * — the perpendicular walls' build-up at the corners.
 */
export function renderInteriorLyingBattenStrips(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	stripWidth: number,
	stripDepth: number,
	material: THREE.Material,
	options: { namePrefix: string; rowSpacing: number },
) {
	renderLyingBattenRows(ctx, wallOpenings, stripWidth, stripDepth, material, {
		namePrefix: options.namePrefix,
		rowSpacing: options.rowSpacing,
		normalCenter: getInnerSurfaceNormalCenter(ctx, stripDepth),
		axisRange: getSurfaceAxisRange(ctx, "inner"),
	});
}

/**
 * Render STANDING glespanel / inv. spikläkt on the inside of a wall — the
 * korsläkt a LYING bastupanel needs (issue 134).
 *
 * The mirror of `renderInteriorLyingBattenStrips`: same layer, same offset
 * from the stud face, boards turned 90°. One run per stud axis, each as tall
 * as the wall is there — the same shape as the facade's `renderFurringStrips`,
 * because it is the same layer on the other face of the same studs, and the
 * same shape the BOM bills it at (`calculateInteriorLiningQuantities`).
 */
export function renderInteriorStandingBattenStrips(
	ctx: StripRenderContext,
	studPositions: number[],
	wallOpenings: WallOpening[],
	stripWidth: number,
	stripDepth: number,
	material: THREE.Material,
	options: { namePrefix: string },
) {
	renderStudAlignedStrips(
		ctx,
		studPositions,
		wallOpenings,
		stripWidth,
		stripDepth,
		material,
		{
			namePrefix: options.namePrefix,
			getNormalCenter: getInnerSurfaceNormalCenter,
		},
	);
}

/**
 * Shared row generator for every lying batten layer (exterior korsläkt spikläkt
 * and interior glespanel). Callers supply the perpendicular position, the span
 * range and the name prefix; row placement, opening subtraction and the
 * roof-profile clip are identical for both.
 */
function renderLyingBattenRows(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	stripWidth: number,
	stripDepth: number,
	material: THREE.Material,
	options: {
		namePrefix: string;
		rowSpacing: number;
		normalCenter: number;
		axisRange: { start: number; end: number };
	},
) {
	const { wall, wallBase } = ctx;
	const normalCenter = options.normalCenter;
	const halfStrip = stripWidth / 2;
	const rowSpacing = options.rowSpacing;

	// Row heights: bottom at syll, then every 600mm, up to just under the wall's
	// HIGHEST point (ridge on a gable end, high eave on a mono side wall). Rows
	// above the low eave survive only where clipRowToRoofProfile finds wall, so
	// the sloping region is battened instead of leaving unfastened panel.
	const maxWallHeight = getMaxWallHeightForWall(ctx);
	const rowCenters: number[] = [];
	rowCenters.push(halfStrip);
	for (let y = rowSpacing; y < maxWallHeight - halfStrip; y += rowSpacing) {
		rowCenters.push(y);
	}
	const topRow = maxWallHeight - halfStrip;
	if (topRow - rowCenters[rowCenters.length - 1] > 0.05) {
		rowCenters.push(topRow);
	}

	for (const rowCenter of rowCenters) {
		// Horizontal segments of this row that survive the openings: start from
		// the caller's span range and subtract each opening's x range where the
		// row height falls inside the opening.
		const segments: VerticalSegment[] = [
			{ start: options.axisRange.start, end: options.axisRange.end },
		];
		for (const opening of wallOpenings) {
			const opStartY = opening.yMm / 1000;
			const opEndY = opStartY + opening.heightMm / 1000;
			if (rowCenter + halfStrip <= opStartY || rowCenter - halfStrip >= opEndY)
				continue;
			subtractVerticalRange(
				segments,
				opening.xMm / 1000,
				opening.xMm / 1000 + opening.widthMm / 1000,
			);
		}

		for (const segment of segments) {
			// Clip the segment to where the roof profile still leaves wall at
			// this height, then render (splitting gable rows at the ridge knee).
			for (const clipped of clipRowToRoofProfile(
				ctx,
				segment,
				rowCenter + halfStrip,
			)) {
				const spanSize = clipped.end - clipped.start;
				if (spanSize <= 0.01) continue;
				const mesh = addWallAlignedBox(
					ctx,
					clipped.start + spanSize / 2,
					wallBase + rowCenter,
					spanSize,
					stripWidth,
					stripDepth,
					material,
					normalCenter,
				);
				mesh.name = `${options.namePrefix}/${wall}-${rowCenter.toFixed(2)}-${clipped.start.toFixed(2)}`;
			}
		}
	}
}

function getMaxWallHeightForWall(ctx: StripRenderContext): number {
	return ctx.roofGeometry.getMaxWallHeight(ctx.wall);
}

/**
 * Clip a horizontal span segment to the sub-range(s) where the wall top
 * (`getWallHeightAt`) is at least `rowTop`, so a lying batten never floats
 * above the sloped/gabled roofline. Splits into two pieces for a gable wall
 * whose peak rises through the middle (a row can survive on both flanks but
 * not across the peak's shoulders).
 */
function clipRowToRoofProfile(
	ctx: StripRenderContext,
	segment: VerticalSegment,
	rowTop: number,
): VerticalSegment[] {
	const SAMPLES = 48;
	const width = segment.end - segment.start;
	if (width <= 0) return [];

	const kept: VerticalSegment[] = [];
	let runStart: number | null = null;
	let prevInside = false;
	for (let i = 0; i <= SAMPLES; i += 1) {
		const localX = segment.start + (width * i) / SAMPLES;
		const inside = getWallHeightAtLocalCenter(ctx, localX) >= rowTop;
		if (inside && !prevInside) {
			runStart = localX;
		} else if (!inside && prevInside && runStart !== null) {
			kept.push({ start: runStart, end: localX });
			runStart = null;
		}
		prevInside = inside;
	}
	if (prevInside && runStart !== null) {
		kept.push({ start: runStart, end: segment.end });
	}
	return kept;
}

/**
 * Åldersbeständig bastufolie — a thin continuous plane laid straight onto the
 * stud faces, under the lying glespanel. Split around openings exactly like the
 * panel it sits behind, so a window reveal never shows a foil sheet crossing it.
 */
export function renderVaporBarrierStrips(
	ctx: StripRenderContext,
	wallOpenings: WallOpening[],
	wallH: number,
	thickness: number,
	namePrefix = "detail/vapor-barrier",
) {
	renderOpeningAwareStrips(
		ctx,
		wallOpenings,
		wallH,
		thickness,
		"inner",
		false,
		namePrefix,
	);
}

/**
 * Stenullsisolering batts filling the stud bays.
 *
 * One box per bay, running between the framing members that bound it, so the
 * batt never interpenetrates a stud, plate, header or opening sill — those
 * overlaps would be visible in exactly the views (framing / section) this layer
 * exists to be seen in. Specifically:
 *
 *  - **Span:** bays run between consecutive stud axes AND between the wall ends
 *    and the outermost axes. The stud generator lays axes on a c/c grid, so on
 *    any wall whose length isn't a multiple of the spacing the last axis stops
 *    short (a 4 m wall's axes end at 3.6 m) — a pairwise-only loop leaves that
 *    terminal section bare.
 *  - **Height:** clamped between the syll's top and the hammarband's underside
 *    (one stud width in from each end, matching how the studs themselves are
 *    generated), then broken around each `horizontalMember` (header / opening
 *    sill) that crosses the bay, not merely around the opening's own rectangle.
 *  - **Depth:** inset 1 mm from the outer stud face and
 *    `ENGINE_WALL_INSULATION_INNER_GAP_M` from the inner one, so no rendered
 *    face is coplanar with a stud face, the wind barrier outside, the
 *    bastufolie inside or the wall pick/tint plane that sits in the inner gap.
 */
export function renderInsulationBays(
	ctx: StripRenderContext,
	studPositions: number[],
	wallOpenings: WallOpening[],
	studWidth: number,
	material: THREE.Material,
	namePrefix = "detail/insulation",
	horizontalMembers: Array<{
		axisStart: number;
		axisEnd: number;
		yStart: number;
		yEnd: number;
	}> = [],
) {
	const { wall, wallBase } = ctx;
	const orientation = getWallOrientation(ctx);
	const OUTER_CLEARANCE_M = 0.001;
	const depth = STUD_D - OUTER_CLEARANCE_M - ENGINE_WALL_INSULATION_INNER_GAP_M;
	const normalCenter =
		orientation.wallPlane(ctx) -
		orientation.outwardSign * (OUTER_CLEARANCE_M + depth / 2);
	const axes = [...new Set(studPositions)].sort((a, b) => a - b);
	if (axes.length === 0) return;

	// Bay boundaries: the wall's own ends plus every stud face. Consecutive
	// pairs of these give every bay, including the leading and trailing ones
	// the stud grid doesn't reach.
	const wallLM = getWallLength(ctx);
	const edges: Array<{ start: number; end: number }> = [];
	edges.push({ start: 0, end: axes[0] - studWidth / 2 });
	for (let index = 0; index + 1 < axes.length; index += 1) {
		edges.push({
			start: axes[index] + studWidth / 2,
			end: axes[index + 1] - studWidth / 2,
		});
	}
	edges.push({ start: axes[axes.length - 1] + studWidth / 2, end: wallLM });

	for (const bay of edges) {
		const bayWidth = bay.end - bay.start;
		if (bayWidth <= 0.02) continue;

		const bayCenter = bay.start + bayWidth / 2;
		const wallHeight = getWallHeightAtLocalCenter(ctx, bayCenter);
		// Between syll top and hammarband underside — the studs are generated
		// over exactly this range.
		const bayBottom = studWidth;
		const bayTop = wallHeight - studWidth;
		if (bayTop - bayBottom <= 0.02) continue;
		const segments: VerticalSegment[] = [{ start: bayBottom, end: bayTop }];

		for (const opening of wallOpenings) {
			const opStart = opening.xMm / 1000;
			const opEnd = opStart + opening.widthMm / 1000;
			if (opEnd <= bay.start + 0.01 || opStart >= bay.end - 0.01) continue;
			subtractVerticalRange(
				segments,
				opening.yMm / 1000,
				opening.yMm / 1000 + opening.heightMm / 1000,
			);
		}
		// Headers and opening sills occupy bay volume above/below the opening
		// itself, so subtract them separately from the opening rectangle.
		for (const member of horizontalMembers) {
			if (
				member.axisEnd <= bay.start + 0.01 ||
				member.axisStart >= bay.end - 0.01
			)
				continue;
			subtractVerticalRange(segments, member.yStart, member.yEnd);
		}

		for (const segment of segments) {
			const segmentHeight = segment.end - segment.start;
			if (segmentHeight <= 0.02) continue;
			const reachesTop = segment.end >= bayTop - 0.01;
			const slopeProfile = reachesTop
				? resolveSlopeProfile(ctx, bay.start, bayWidth)
				: null;
			const mesh = addWallAlignedBox(
				ctx,
				bayCenter,
				wallBase + segment.start + segmentHeight / 2,
				bayWidth,
				segmentHeight,
				depth,
				material,
				normalCenter,
				{ slopeProfile: slopeProfile ?? undefined },
			);
			mesh.name = `${namePrefix}/${wall}-${bay.start.toFixed(2)}-${segment.start.toFixed(2)}`;
		}
	}
}

// ─── Internal Utilities ─────────────────────────────────────

function getWallLength(ctx: StripRenderContext): number {
	return ctx.wall === "front" || ctx.wall === "back" ? ctx.W : ctx.L;
}
